import React, { useCallback, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@paintscout/ui'
import { makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import DesktopMacIcon from '@material-ui/icons/DesktopMac'
import SyncAltIcon from '@material-ui/icons/SyncAlt'
import DnsIcon from '@material-ui/icons/Dns'
import FaceIcon from '@material-ui/icons/Face'
import CloseIcon from '@material-ui/icons/Close'
import type { Permission } from 'paintscout'
import { getPermissionsNext, getPermissions, CUSTOM_PERMISSIONS_BY_ROLE } from '@paintscout/util/builder'
import classnames from 'classnames'

// ─── Types ────────────────────────────────────────────────────────────────────

type OldRole = 'admin' | 'sales' | 'viewer' | 'crew'
type NewRole = 'core' | 'team' | 'painter'
type MockRole = OldRole | NewRole

export interface DevPermissionsState {
  active: boolean
  role: MockRole
  customPermissions: Permission[]
  isOwner: boolean
  dismissed?: boolean
  collapsed?: boolean
}

const STORAGE_KEY = 'ps:devtools:permissions'
const POSITION_KEY = 'ps:devtools:position'

const OLD_ROLES: OldRole[] = ['admin', 'sales', 'viewer', 'crew']
const NEW_ROLES: NewRole[] = ['core', 'team', 'painter']

const ROLE_DISPLAY_NAMES: Partial<Record<MockRole, string>> = {
  core: 'sales',
  sales: 'sales (legacy)'
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    zIndex: 99999,
    userSelect: 'none',
    minWidth: 280,
    maxWidth: 340
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1),
    cursor: 'grab',
    touchAction: 'none',
    '&:active': { cursor: 'grabbing' },
    background: theme.palette.grey[600],
    color: '#fff',
    borderRadius: '4px 4px 0 0'
  },
  headerActive: {
    background: theme.palette.success.main
  },
  headerActiveFullstack: {
    background: 'linear-gradient(135deg, #7c3aed 0%, #0ea5e9 100%)'
  },
  titleBlock: {
    marginLeft: 8,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  title: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: 'uppercase'
  },
  iconGroup: {
    display: 'flex',
    gap: 4,
    color: '#ffffff55',
    '& svg': { fontSize: 16 }
  },
  activeIcon: {
    color: '#fff'
  },
  tabs: {
    minHeight: 32,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-root': {
      minHeight: 32,
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.5
    }
  },
  body: {
    padding: theme.spacing(1.5),
    maxHeight: 380,
    overflowY: 'auto'
  },
  roleGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1)
  },
  roleBtn: {
    padding: theme.spacing(0.5, 1),
    fontSize: 12,
    fontWeight: 500,
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 4,
    cursor: 'pointer',
    textAlign: 'center',
    background: 'transparent',
    transition: 'all 0.15s',
    '&:hover': { borderColor: theme.palette.grey[600] }
  },
  roleBtnSelected: {
    background: theme.palette.grey[400],
    borderColor: theme.palette.grey[400],
    color: '#fff'
  },
  roleBtnSelectedActive: {
    background: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    color: '#fff'
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  permChip: {
    fontSize: 12,
    height: 20,
    margin: 2
  },
  permList: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(0.5)
  },
  customPerm: {
    display: 'block',
    '& .MuiFormControlLabel-label': { fontSize: 12 }
  },
  subCustomPerm: {
    paddingLeft: theme.spacing(3)
  }
}))

// ─── Helpers ──────────────────────────────────────────────────────────────────

function loadState(): DevPermissionsState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch (err) {
    console.log(err)
  }
  return { active: false, role: 'admin', customPermissions: [], isOwner: false, dismissed: false }
}

function saveState(state: DevPermissionsState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

function loadPosition(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(POSITION_KEY)
    if (raw) return JSON.parse(raw)
  } catch (err) {
    console.log(err)
  }
  return { x: window.innerWidth - 360, y: 80 }
}

function resolvePermissionsForRole(role: MockRole, customPermissions: Permission[], isOwner: boolean): Permission[] {
  if ((NEW_ROLES as string[]).includes(role)) {
    return getPermissionsNext([role], customPermissions, isOwner)
  }
  // Old roles: getPermissions stamps from optionsDoc but the core role permissions
  // don't depend on it, so pass an empty stub for dev purposes
  return getPermissions({} as any, [role])
}

function buildHasPermissions(permissions: Permission[]) {
  return (perms: Permission[], any?: boolean): boolean => {
    let hasAny = false
    let hasAll = true
    perms.forEach((p) => {
      const has = permissions.includes(p)
      if (!has) hasAll = false
      if (has) hasAny = true
    })
    return any ? hasAny : hasAll
  }
}

// ─── Context for overrides ────────────────────────────────────────────────────

export interface DevPermissionsContextValue {
  state: DevPermissionsState
  setState: (s: DevPermissionsState) => void
  overrideHasPermissions: ReturnType<typeof buildHasPermissions> | null
  effectivePermissions: Permission[]
}

export const DevPermissionsContext = React.createContext<DevPermissionsContextValue>({
  state: { active: false, role: 'admin', customPermissions: [], isOwner: false, dismissed: false },
  setState: () => {},
  overrideHasPermissions: null,
  effectivePermissions: []
})

// ─── Provider ─────────────────────────────────────────────────────────────────

export function DevPermissionsProvider({ children }: { children: React.ReactNode }) {
  const [state, setStateRaw] = useState<DevPermissionsState>(loadState)

  const setState = useCallback((next: DevPermissionsState) => {
    setStateRaw(next)
    saveState(next)
  }, [])

  const effectivePermissions = state.active
    ? resolvePermissionsForRole(state.role, state.customPermissions, state.isOwner)
    : []

  const overrideHasPermissions = state.active ? buildHasPermissions(effectivePermissions) : null

  return (
    <DevPermissionsContext.Provider value={{ state, setState, effectivePermissions, overrideHasPermissions }}>
      {children}
    </DevPermissionsContext.Provider>
  )
}

// ─── Widget ───────────────────────────────────────────────────────────────────

export function DevPermissionsOverlay({
  state,
  setState,
  effectivePermissions,
  realPermissions,
  realRole,
  onMockChange
}: {
  state: DevPermissionsState
  setState: (s: DevPermissionsState) => void
  effectivePermissions: Permission[]
  realPermissions: Permission[]
  realRole: string
  onMockChange?: () => void
}) {
  const classes = useStyles()
  const [pos, setPos] = useState(loadPosition)
  const collapsed = state.collapsed ?? false
  const toggleCollapsed = () => setState({ ...state, collapsed: !collapsed })
  const [tab, setTab] = useState<'select' | 'resolved'>('select')
  const dragging = useRef(false)
  const dragOffset = useRef({ x: 0, y: 0 })
  const preOwnerPerms = useRef<Permission[]>([])

  const isNewRole = (NEW_ROLES as string[]).includes(state.role)

  const savePos = useCallback((p: { x: number; y: number }) => {
    setPos(p)
    localStorage.setItem(POSITION_KEY, JSON.stringify(p))
  }, [])

  const clamp = useCallback(
    (clientX: number, clientY: number) => ({
      x: Math.max(0, Math.min(window.innerWidth - 300, clientX - dragOffset.current.x)),
      y: Math.max(0, Math.min(window.innerHeight - 60, clientY - dragOffset.current.y))
    }),
    []
  )

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      dragging.current = true
      dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }

      const onMove = (ev: MouseEvent) => {
        if (!dragging.current) return
        setPos(clamp(ev.clientX, ev.clientY))
      }
      const onUp = (ev: MouseEvent) => {
        dragging.current = false
        savePos(clamp(ev.clientX, ev.clientY))
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [pos, savePos, clamp]
  )

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0]
      dragging.current = true
      dragOffset.current = { x: touch.clientX - pos.x, y: touch.clientY - pos.y }

      const onMove = (ev: TouchEvent) => {
        if (!dragging.current) return
        ev.preventDefault()
        const t = ev.touches[0]
        setPos(clamp(t.clientX, t.clientY))
      }
      const onEnd = (ev: TouchEvent) => {
        dragging.current = false
        const t = ev.changedTouches[0]
        savePos(clamp(t.clientX, t.clientY))
        window.removeEventListener('touchmove', onMove)
        window.removeEventListener('touchend', onEnd)
      }
      window.addEventListener('touchmove', onMove, { passive: false })
      window.addEventListener('touchend', onEnd)
    },
    [pos, savePos, clamp]
  )

  const handleSetState = (next: DevPermissionsState) => {
    setState(next)
    if (next.active) {
      onMockChange?.()
    }
  }

  const setRole = (role: MockRole) => {
    handleSetState({ ...state, role, customPermissions: [], isOwner: false })
  }

  const toggleCustomPerm = (perm: Permission) => {
    const next = state.customPermissions.includes(perm)
      ? state.customPermissions.filter((p) => p !== perm)
      : [...state.customPermissions, perm]
    handleSetState({ ...state, customPermissions: next })
  }

  const effectiveRoleRaw = state.active ? state.role : realRole
  const effectiveRole = ROLE_DISPLAY_NAMES[effectiveRoleRaw as MockRole] ?? effectiveRoleRaw

  if (state.dismissed) return null

  return ReactDOM.createPortal(
    <div className={classes.root} style={{ left: pos.x, top: pos.y }}>
      <Paper elevation={8}>
        {/* Header / drag handle */}
        <div
          className={`${classes.header} ${state.active ? (realRole === 'superadmin' ? classes.headerActiveFullstack : classes.headerActive) : ''}`}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        >
          <FaceIcon style={{ fontSize: 24 }} />
          <span className={classes.titleBlock}>
            <div className={classes.title}>{state.active ? `Mocking: ${effectiveRole}` : `Real: ${effectiveRole}`}</div>
            <Tooltip
              disabled={!state.active}
              hideIcon
              title={realRole === 'superadmin' ? 'Mocking front end and api' : 'Mocking front end only'}
              placement="bottom-start"
            >
              <div className={classes.iconGroup}>
                <DesktopMacIcon className={classnames({ [classes.activeIcon]: state.active })} />
                <SyncAltIcon
                  className={classnames({ [classes.activeIcon]: state.active && realRole === 'superadmin' })}
                />
                <DnsIcon className={classnames({ [classes.activeIcon]: state.active && realRole === 'superadmin' })} />
              </div>
            </Tooltip>
          </span>
          <Switch
            size="small"
            checked={state.active}
            onChange={(e) => handleSetState({ ...state, active: e.target.checked })}
            color="default"
            onMouseDown={(e) => e.stopPropagation()}
          />
          <IconButton
            size="small"
            style={{ color: '#fff', padding: 2 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={toggleCollapsed}
          >
            {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            style={{ color: '#fff', padding: 2 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setState({ ...state, dismissed: true })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {!collapsed && (
          <>
            <Tabs
              value={tab}
              onChange={(_e, v) => setTab(v)}
              className={classes.tabs}
              indicatorColor="primary"
              textColor="primary"
              variant={'fullWidth'}
            >
              <Tab value="select" label="Select" />
              <Tab value="resolved" label="Resolved" />
            </Tabs>

            <div className={classes.body}>
              {tab === 'select' && (
                <>
                  {/* Old roles */}
                  <Typography className={classes.sectionLabel}>Legacy roles</Typography>
                  <div className={classes.roleGrid}>
                    {OLD_ROLES.map((r) => (
                      <button
                        key={r}
                        className={classnames(classes.roleBtn, {
                          [classes.roleBtnSelected]: state.role === r,
                          [classes.roleBtnSelectedActive]: state.role === r && state.active
                        })}
                        onClick={() => setRole(r)}
                      >
                        {ROLE_DISPLAY_NAMES[r] ?? r}
                      </button>
                    ))}
                  </div>

                  {/* New roles */}
                  <Typography className={classes.sectionLabel} style={{ marginTop: 8 }}>
                    New roles
                  </Typography>
                  <div className={classes.roleGrid}>
                    {NEW_ROLES.map((r) => (
                      <button
                        key={r}
                        className={classnames(classes.roleBtn, {
                          [classes.roleBtnSelected]: state.role === r,
                          [classes.roleBtnSelectedActive]: state.role === r && state.active
                        })}
                        onClick={() => setRole(r)}
                      >
                        {ROLE_DISPLAY_NAMES[r] ?? r}
                      </button>
                    ))}
                  </div>

                  {/* New role options */}
                  {isNewRole && (
                    <>
                      <Divider />
                      {state.role === 'core' && (
                        <>
                          <Typography className={classes.sectionLabel} style={{ marginTop: 4 }}>
                            Owner
                          </Typography>
                          <FormControlLabel
                            className={classes.customPerm}
                            control={
                              <Checkbox
                                checked={state.isOwner}
                                onChange={(e) => {
                                  const isOwner = e.target.checked
                                  if (isOwner) {
                                    preOwnerPerms.current = state.customPermissions
                                  }
                                  handleSetState({
                                    ...state,
                                    isOwner,
                                    customPermissions: isOwner
                                      ? [...(CUSTOM_PERMISSIONS_BY_ROLE['core'] ?? [])]
                                      : preOwnerPerms.current
                                  })
                                }}
                              />
                            }
                            label="isOwner"
                          />
                        </>
                      )}
                      {CUSTOM_PERMISSIONS_BY_ROLE[state.role as NewRole]?.length > 0 && (
                        <>
                          <Typography className={classes.sectionLabel} style={{ marginTop: 4 }}>
                            Custom permissions
                          </Typography>
                          {CUSTOM_PERMISSIONS_BY_ROLE[state.role as NewRole].map((perm) => {
                            // Rendered as sub-items under their parent permissions below
                            if (
                              perm === 'accept-payment:assigned-documents' ||
                              perm === 'write:account-settings-billing' ||
                              perm === 'write:account-settings-user-management'
                            )
                              return null

                            const billingPairChecked =
                              state.isOwner ||
                              (state.customPermissions.includes('write:account-settings-billing') &&
                                state.customPermissions.includes('write:account-settings-user-management'))
                            const toggleBillingPair = () => {
                              const hasBoth =
                                state.customPermissions.includes('write:account-settings-billing') &&
                                state.customPermissions.includes('write:account-settings-user-management')
                              const next = hasBoth
                                ? state.customPermissions.filter(
                                    (p) =>
                                      p !== 'write:account-settings-billing' &&
                                      p !== 'write:account-settings-user-management'
                                  )
                                : [
                                    ...state.customPermissions.filter(
                                      (p) =>
                                        p !== 'write:account-settings-billing' &&
                                        p !== 'write:account-settings-user-management'
                                    ),
                                    'write:account-settings-billing' as Permission,
                                    'write:account-settings-user-management' as Permission
                                  ]
                              handleSetState({ ...state, customPermissions: next })
                            }
                            const isAccountSettings = perm === 'write:account-settings'
                            const toggleAccountSettings = () => {
                              const isCurrentlyChecked = state.customPermissions.includes('write:account-settings')
                              const next = isCurrentlyChecked
                                ? state.customPermissions.filter(
                                    (p) =>
                                      p !== 'write:account-settings' &&
                                      p !== 'write:account-settings-billing' &&
                                      p !== 'write:account-settings-user-management'
                                  )
                                : [...state.customPermissions, 'write:account-settings' as Permission]
                              handleSetState({ ...state, customPermissions: next })
                            }
                            const isViewJobValue = perm === 'view:job-value'
                            const toggleViewJobValue = () => {
                              const isCurrentlyChecked = state.customPermissions.includes('view:job-value')
                              const next = isCurrentlyChecked
                                ? state.customPermissions.filter(
                                    (p) => p !== 'view:job-value' && p !== 'accept-payment:assigned-documents'
                                  )
                                : [...state.customPermissions, 'view:job-value' as Permission]
                              handleSetState({ ...state, customPermissions: next })
                            }
                            return (
                              <React.Fragment key={perm}>
                                <FormControlLabel
                                  className={classes.customPerm}
                                  disabled={state.isOwner}
                                  control={
                                    <Checkbox
                                      checked={state.isOwner || state.customPermissions.includes(perm)}
                                      onChange={
                                        isAccountSettings
                                          ? toggleAccountSettings
                                          : isViewJobValue
                                            ? toggleViewJobValue
                                            : () => toggleCustomPerm(perm)
                                      }
                                      disabled={state.isOwner}
                                    />
                                  }
                                  label={perm}
                                />
                                {isAccountSettings && (
                                  <>
                                    <FormControlLabel
                                      className={`${classes.customPerm} ${classes.subCustomPerm}`}
                                      disabled={
                                        state.isOwner || !state.customPermissions.includes('write:account-settings')
                                      }
                                      control={
                                        <Checkbox
                                          checked={billingPairChecked}
                                          onChange={toggleBillingPair}
                                          disabled={
                                            state.isOwner || !state.customPermissions.includes('write:account-settings')
                                          }
                                        />
                                      }
                                      label="write:account-settings-billing"
                                    />
                                    <FormControlLabel
                                      className={`${classes.customPerm} ${classes.subCustomPerm}`}
                                      disabled={
                                        state.isOwner || !state.customPermissions.includes('write:account-settings')
                                      }
                                      control={
                                        <Checkbox
                                          checked={billingPairChecked}
                                          onChange={toggleBillingPair}
                                          disabled={
                                            state.isOwner || !state.customPermissions.includes('write:account-settings')
                                          }
                                        />
                                      }
                                      label="write:account-settings-user-management"
                                    />
                                  </>
                                )}
                                {isViewJobValue && (
                                  <FormControlLabel
                                    className={`${classes.customPerm} ${classes.subCustomPerm}`}
                                    disabled={state.isOwner || !state.customPermissions.includes('view:job-value')}
                                    control={
                                      <Checkbox
                                        checked={state.customPermissions.includes('accept-payment:assigned-documents')}
                                        onChange={() => toggleCustomPerm('accept-payment:assigned-documents')}
                                        disabled={state.isOwner || !state.customPermissions.includes('view:job-value')}
                                      />
                                    }
                                    label="accept-payment:assigned-documents"
                                  />
                                )}
                              </React.Fragment>
                            )
                          })}
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {tab === 'resolved' &&
                (() => {
                  const displayPerms = state.active ? effectivePermissions : realPermissions
                  const groups: Array<{ label: string; match: (p: string) => boolean }> = [
                    { label: 'Documents', match: (p) => p.includes('document') },
                    { label: 'Quotes & Invoices', match: (p) => p.includes('quote') || p.includes('invoice') },
                    { label: 'CRM', match: (p) => p.includes('event') || p.includes('task') || p.includes('deal') },
                    { label: 'Contacts & Companies', match: (p) => p.includes('contact') || p.includes('compan') },
                    { label: 'Settings', match: () => true }
                  ]
                  let remaining = [...displayPerms]
                  const grouped = groups.map(({ label, match }) => {
                    const perms = remaining.filter(match)
                    remaining = remaining.filter((p) => !match(p))
                    return { label, perms }
                  })
                  return (
                    <>
                      <Typography className={classes.sectionLabel} style={{ marginBottom: 4 }}>
                        {`${displayPerms.length} permissions`}
                      </Typography>
                      {grouped.map(({ label, perms }) =>
                        perms.length === 0 ? null : (
                          <Box key={label} mb={1}>
                            <Typography className={classes.sectionLabel}>{label}</Typography>
                            <Box className={classes.permList}>
                              {perms.map((p) => (
                                <Chip key={p} label={p} size="small" className={classes.permChip} />
                              ))}
                            </Box>
                          </Box>
                        )
                      )}
                    </>
                  )
                })()}
            </div>
          </>
        )}
      </Paper>
    </div>,
    document.body
  )
}
